import { FastForwardOutlined } from "@ant-design/icons";
import { Flex, Select } from "antd";
import { FC, useCallback, useState } from "react";

import { BaseModal } from "@/components/BaseModal";
import { PrimaryText, SecondaryText } from "@/components/Typography/Styled";
import { COMMENT_OPTIONS } from "@/constants/ui/comment";

import { StyledButton } from "./Styled";

interface ForceStepCompleteProps {
    onComplete: (comment: string) => Promise<void>;
    taskName: string;
    marketName: string;
    entity?: "task" | "step";
}

export const ForceStepComplete: FC<ForceStepCompleteProps> = ({
    onComplete,
    taskName,
    marketName,
    entity = "task",
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [comment, setComment] = useState("");
    const [loading, setLoading] = useState(false);

    const handleClose = useCallback(() => {
        if (loading) return;

        setIsModalOpen(false);
    }, [loading]);

    const handleChangeComment = useCallback((value: string) => {
        const currentOptionComment = COMMENT_OPTIONS.find(
            (option) => option.value === value,
        ) ?? { label: "" };

        setComment(currentOptionComment.label);
    }, []);

    const handleConfirm = useCallback(async () => {
        if (!comment) return;

        try {
            setLoading(true);
            await onComplete(comment);
        } catch {
            return;
        } finally {
            setLoading(false);
            handleClose();
        }
    }, [comment, handleClose, onComplete]);

    return (
        <>
            <StyledButton
                icon={<FastForwardOutlined />}
                type="text"
                onClick={() => setIsModalOpen(true)}
                iconPosition={"end"}
            >
                <PrimaryText $textColor="error" fontWeight="500" fontSize={14}>
                    Пропустити
                </PrimaryText>
            </StyledButton>

            <BaseModal
                open={isModalOpen}
                onClose={handleClose}
                title={
                    entity === "step"
                        ? "Швидке завершення кроку"
                        : "Швидке завершення задачі"
                }
                confirmText="Завершити"
                confirmDisabled={!comment}
                confirmLoading={loading}
                maskClosable={!loading}
                onConfirm={handleConfirm}
            >
                <Flex vertical gap={8}>
                    <PrimaryText fontSize={16}>{taskName}</PrimaryText>
                    <SecondaryText>{marketName}</SecondaryText>
                </Flex>

                <Flex vertical gap={4}>
                    <SecondaryText>Коментар:</SecondaryText>
                    <Select
                        onChange={handleChangeComment}
                        placeholder="Залиште коментар"
                        options={COMMENT_OPTIONS}
                        size="large"
                        disabled={loading}
                    />
                </Flex>
            </BaseModal>
        </>
    );
};
